import api from "../utils/api";
import { getAuthToken } from "../utils/tokens";

/**
 * Get all users with pagination and filters (Admin only)
 * @param {Object} params - Query parameters
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.limit=10] - Items per page
 * @param {string} [params.search] - Search term for name or email
 * @param {number} [params.role] - Filter by role (0=user, 1=admin)
 * @param {boolean} [params.isDeleted] - Filter by deletion status
 * @returns {Promise<Object>} Users with pagination info
 */
const listUsers = async (params = {}) => {
  // console.log("params: ", params);
  try {
    const token = getAuthToken();
    // Set default params if not provided
    const finalParams = {
      page: 1,
      limit: 10,
      ...params,
    };
    const response = await api.get("/admin/users", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: finalParams,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new user (Admin only)
 * @param {Object} userData - User data
 * @param {string} userData.name - User's name
 * @param {string} userData.email - User's email
 * @param {string} userData.phoneNo - User's phone number
 * @param {string} userData.password - User's password
 * @param {number} [userData.role=0] - User role (0=user, 1=admin)
 * @param {Object} [userData.address] - User's address
 * @returns {Promise<Object>} Created user data
 */
const createUser = async (userData) => {
  try {
    // console.log("user data to be: ", userData);
    const token = getAuthToken();
    const response = await api.post("/admin/users", userData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update user by ID (Admin only)
 * @param {string} userId - User ID
 * @param {Object} userData - User data to update
 * @param {string} [userData.name] - User's name
 * @param {string} [userData.email] - User's email
 * @param {string} [userData.password] - New password
 * @param {number} [userData.role] - User role (0=user, 1=admin)
 * @param {Object} [userData.address] - User's address
 * @param {boolean} [userData.isDeleted] - Deletion status
 * @returns {Promise<Object>} Updated user data
 */
const updateUser = async (userId, userData) => {
  try {
    // console.log("user data: ", userData);
    const token = getAuthToken();
    const response = await api.put(`/admin/users/${userId}`, userData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete user by ID (Admin only - hard delete)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Success message
 */
// HARD DELETE
const deleteUserAdmin = async (userId) => {
  try {
    const token = getAuthToken();
    const response = await api.delete(`/admin/users/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get user by ID (Admin only)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User data
 */
const getUserById = async (userId) => {
  try {
    const token = getAuthToken();
    const response = await api.get(`/admin/users/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Soft delete user by ID (Admin only)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Success message and updated user data
 */
const softDeleteUserAdmin = async (userId) => {
  try {
    const token = getAuthToken();
    const response = await api.post(
      `/admin/users/${userId}/soft-delete`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export {
  listUsers,
  createUser,
  updateUser,
  deleteUserAdmin,
  getUserById,
  softDeleteUserAdmin,
};
